import React, { useMemo, useState } from 'react';

function buildViewerUrl(fileUrl) {
  const trimmed = (fileUrl || '').trim();
  if (!trimmed) return '';
  const lower = trimmed.split('?')[0].toLowerCase();
  if (lower.endsWith('.pdf')) {
    return trimmed;
  }
  return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(trimmed)}`;
}

export default function DocumentFileViewer({ url, fallbackText, title = 'Document preview' }) {
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const viewerUrl = useMemo(() => buildViewerUrl(url), [url]);

  if (!url) {
    return (
      <div className="bg-gray-900 rounded-lg p-4 h-96 overflow-y-auto">
        <pre className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">
          {fallbackText || 'Document preview not available.'}
        </pre>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="bg-gray-900 rounded-lg p-4 min-h-[24rem] flex flex-col gap-4">
        {fallbackText ? (
          <pre className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed flex-1 overflow-y-auto">
            {fallbackText}
          </pre>
        ) : (
          <p className="text-gray-400 text-sm">Could not preview this document in the browser.</p>
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-400 hover:text-purple-300 font-medium text-sm self-start"
        >
          Open document in new tab
        </a>
      </div>
    );
  }

  return (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden min-h-[24rem] h-[32rem]">
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading document preview...</p>
          </div>
        </div>
      ) : null}
      <iframe
        title={title}
        src={viewerUrl}
        className="w-full h-full border-0 bg-white"
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setFailed(true);
        }}
      />
      <div className="absolute bottom-3 right-3 z-20">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-gray-800/90 hover:bg-gray-700 text-purple-300 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-600"
        >
          Open in browser
        </a>
      </div>
    </div>
  );
}
